/**
 * Turbo-Cat™ (= Format-finder) — fills .faf slots from file-format evidence
 * across ~200 formats (the KNOWLEDGE_BASE). Restores the multi-ecosystem
 * manifest interrogation the v6.0 rewrite narrowed to README+Cargo+package.json.
 *
 * v6-native sync port (the v5 engine was async; sync integrates cleanly with
 * `auto`). Two-layer: (1A) config files in the project directory itself,
 * (1B) source extensions below it; priority-wins slot recommendations.
 *
 * Boundary: detection never looks above the project directory. A parent's
 * tsconfig.json, Cargo.toml or vercel.json is another project's evidence —
 * a monorepo package, a subfolder or a new folder under ~ must not inherit it.
 *
 * Used as the LOWEST-precedence filler in `auto`: v6's specific detection wins;
 * Turbo-Cat fills only the slots still empty (esp. non-npm stacks).
 */

import type { Dirent } from 'fs';
import { join, extname, resolve } from 'path';
import { readRepoDir, readRepoFile, statRepoFile, withRepoRoot } from '../core/safe-write.js';
import { KNOWLEDGE_BASE } from './turbo-cat-knowledge.js';
import { detectDartProject } from './dart.js';
import { detectGoProject } from './go.js';
import { detectCsharpProject } from './csharp.js';
import { detectJvmProject } from './jvm.js';
import { detectRubyProject } from './ruby.js';
import { detectSwiftProject } from './swift.js';

const IGNORE = new Set([
  'node_modules', '.git', 'dist', 'build', 'venv', '.venv', '__pycache__',
  'target', 'coverage', '.next', '.svelte-kit', 'vendor',
]);
const SCAN_EXTS = new Set([
  '.py', '.ts', '.tsx', '.js', '.jsx', '.svelte', '.vue',
  '.rb', '.go', '.rs', '.php', '.java', '.kt', '.swift',
]);

/** ContextSlots (camelCase + snake_case) → v6 .faf slot path. */
const SLOT_MAP: Record<string, [section: string, field: string]> = {
  mainLanguage: ['project', 'main_language'], main_language: ['project', 'main_language'],
  framework: ['stack', 'frontend'],
  cssFramework: ['stack', 'css_framework'], css: ['stack', 'css_framework'],
  uiLibrary: ['stack', 'ui_library'], ui_library: ['stack', 'ui_library'],
  stateManagement: ['stack', 'state_management'], state: ['stack', 'state_management'],
  backend: ['stack', 'backend'],
  apiType: ['stack', 'api_type'], api: ['stack', 'api_type'],
  runtime: ['stack', 'runtime'],
  database: ['stack', 'database'], db: ['stack', 'database'],
  connection: ['stack', 'connection'],
  hosting: ['stack', 'hosting'],
  buildTool: ['stack', 'build'], build_tool: ['stack', 'build'], build: ['stack', 'build'],
  packageManager: ['stack', 'package_manager'], pkg_manager: ['stack', 'package_manager'],
  cicd: ['stack', 'cicd'],
};

/** A format actually discovered on disk (config-file layer; real files only). */
export interface DiscoveredFormat {
  fileName: string;
  /** Derived from the entry's primary slot family (deterministic — real
   *  knowledge surfaced, not invented): package-manager, language, framework,
   *  backend, database, ci-cd, hosting, build, … */
  category: string;
  priority: number;
}

export interface TurboCatResult {
  slotFills: Record<string, string>; // ContextSlots key → value (priority-wins)
  frameworks: string[];
  confirmedCount: number;
  /** Option B (compose spec 2026-06-12): the per-format breakdown consumers
   *  display — surfaced so MCPs can DELETE their local format maps entirely. */
  discoveredFormats: DiscoveredFormat[];
  /** Deterministic lowercase signature, e.g. "typescript-react" ('unknown-stack' when bare). */
  stackSignature: string;
}

interface FoundFormat {
  slots: Record<string, string>;
  priority: number;
  frameworks: string[];
  fileName?: string; // set for the config-file layer (real files on disk)
}

/** slot-family → display category (first match wins; deterministic). */
const CATEGORY_BY_SLOT: Array<[slot: string, category: string]> = [
  ['packageManager', 'package-manager'],
  ['pkg_manager', 'package-manager'],
  ['mainLanguage', 'language'],
  ['main_language', 'language'],
  ['framework', 'framework'],
  ['backend', 'backend'],
  ['database', 'database'],
  ['db', 'database'],
  ['cicd', 'ci-cd'],
  ['hosting', 'hosting'],
  ['buildTool', 'build'],
  ['build_tool', 'build'],
  ['build', 'build'],
  ['runtime', 'runtime'],
  ['cssFramework', 'css'],
  ['css', 'css'],
  ['uiLibrary', 'ui-library'],
  ['stateManagement', 'state-management'],
  ['apiType', 'api'],
  ['api', 'api'],
];

function categoryFor(slots: Record<string, string>, frameworks: string[]): string {
  for (const [slot, category] of CATEGORY_BY_SLOT) {
    if (slot in slots) {return category;}
  }
  return frameworks.length > 0 ? 'framework' : 'config';
}

/**
 * `manifest.json` is one of the most overloaded filenames in software —
 * Chrome/browser extension, mcpb/MCP manifest, PWA manifest, or plain config.
 * Asserting a Chrome stack from the FILENAME is a guess, and sourced-only
 * forbids guessing (every FAF MCP ships an mcpb manifest.json and was
 * mis-detected as a JS Chrome Extension — the 2026-06-12 blocker).
 *
 * Disambiguate by CONTENT, or assert nothing:
 *   chrome  — manifest_version is a NUMBER (2/3) AND a chrome field present
 *             → the KB's chrome entry applies (as before)
 *   mcpb    — manifest_version is a STRING ("0.x") or MCP fields present
 *             → assert NOTHING (real evidence — .ts/tsconfig/deps — wins)
 *   pwa     — start_url/display/icons → assert nothing
 *   unknown/unreadable → assert NOTHING. An honest empty beats a guessed stack.
 */
function classifyManifestJson(dir: string, name: string): 'chrome' | 'other' {
  try {
    const m = JSON.parse(readRepoFile(dir, name) ?? '') as Record<string, unknown>;
    const chromeField = ['background', 'content_scripts', 'action', 'browser_action', 'permissions']
      .some((k) => k in m);
    if (typeof m.manifest_version === 'number' && chromeField) {return 'chrome';}
    return 'other'; // mcpb (string manifest_version / server / tools / display_name), PWA, or plain config
  } catch {
    return 'other'; // unreadable → assert nothing
  }
}

/** A directory entry that is a regular file (a link counts when it leads to one
 *  inside the project; a dangling link, or one out of it, does not).
 *  A folder named `go.mod` or `package.json` is not evidence of anything. */
function isFileEntry(dir: string, e: Dirent): boolean {
  if (e.isFile()) {return true;}
  if (!e.isSymbolicLink()) {return false;}
  return statRepoFile(dir, e.name)?.isFile() === true;
}

/** A directory entry that is a folder (a link counts when it leads to one inside the project). */
function isDirEntry(dir: string, e: Dirent): boolean {
  if (e.isDirectory()) {return true;}
  if (!e.isSymbolicLink()) {return false;}
  return statRepoFile(dir, e.name)?.isDirectory() === true;
}

/** Layer 1A — config files in the project directory only. Never a parent:
 *  the walk up to 10 ancestors (stopping only at the project's own .git) wrote
 *  another tree's stack into project.faf. */
function scanConfigFiles(projectDir: string): FoundFormat[] {
  const found: FoundFormat[] = [];
  const cur = resolve(projectDir);
  /** One JVM classification per directory (pom + gradle + settings would triple-fire). */
  const jvmDirsDone = new Set<string>();
  const entries = readRepoDir(cur);
  if (!entries) {return found;}
  for (const e of entries) {
    const f = e.name;
    // Content-aware: *.xcodeproj is a folder (checked below); every other
    // format is a file, so anything else by that name is not evidence.
    if (f.endsWith('.xcodeproj') ? !isDirEntry(cur, e) : !isFileEntry(cur, e)) {continue;}
    // Content-aware: any *.csproj (names vary — not in KNOWLEDGE_BASE by basename).
    // .csproj alone ≠ type: Sdk + packages via detectCsharpProject.
    if (f.toLowerCase().endsWith('.csproj')) {
      const cp = detectCsharpProject(cur);
      if (cp) {
        const slots: Record<string, string> = {
          mainLanguage: 'C#',
          packageManager: 'NuGet',
          buildTool: 'dotnet',
        };
        if (cp.appType === 'backend' && cp.framework) {
          slots.backend = cp.framework;
        } else if (cp.appType === 'cli' && cp.framework) {
          slots.framework = cp.framework;
        } else if (cp.appType === 'mcp') {
          slots.apiType = 'MCP';
        } else if (cp.appType === 'mobile' && cp.framework) {
          slots.framework = cp.framework;
        }
        if (cp.targetFramework) {
          slots.runtime = cp.targetFramework;
        }
        const frameworks = cp.framework ? [cp.framework, 'C#', '.NET'] : ['C#', '.NET'];
        found.push({ slots, priority: 36, frameworks, fileName: f });
      }
      continue;
    }

    // Content-aware: *.xcodeproj (directory) — light productType via detectSwiftProject.
    // Prefer Package.swift when both exist (handled below); xcodeproj covers app-only trees.
    if (f.endsWith('.xcodeproj')) {
      const sp = detectSwiftProject(cur);
      if (sp) {
        const slots: Record<string, string> = {
          mainLanguage: 'Swift',
          packageManager: sp.packageManager === 'spm' ? 'spm' : 'Xcode',
          buildTool: sp.packageManager.includes('spm') ? 'swift build' : 'xcodebuild',
        };
        if (sp.appType === 'backend' && sp.framework) {
          slots.backend = sp.framework;
        } else if (sp.appType === 'cli' && sp.framework) {
          slots.framework = sp.framework;
        } else if (sp.appType === 'mcp') {
          slots.apiType = 'MCP';
        } else if (sp.appType === 'app' && sp.framework) {
          slots.framework = sp.framework;
        }
        const frameworks = sp.framework ? [sp.framework, 'Swift'] : ['Swift'];
        found.push({ slots, priority: 36, frameworks, fileName: f });
      }
      continue;
    }

    const k = KNOWLEDGE_BASE[f];
    if (!k) {continue;}
    // Sourced-only gate: manifest.json only asserts the chrome stack when
    // its CONTENT proves a chrome extension; mcpb/PWA/unknown assert nothing.
    if (f === 'manifest.json' && classifyManifestJson(cur, f) !== 'chrome') {continue;}
    // Content-aware: pubspec.yaml backs Flutter apps, Dart CLIs/packages,
    // servers, and MCP servers — branch by deps instead of asserting Flutter
    // for everything (the filename-only flattening this engine used to do).
    if (f === 'pubspec.yaml') {
      const dp = detectDartProject(cur);
      if (dp) {
        const slots: Record<string, string> = { mainLanguage: 'Dart', packageManager: 'pub' };
        if (dp.isFlutter) {
          slots.framework = 'Flutter';
          if (dp.stateManagement) {slots.stateManagement = dp.stateManagement;}
        } else if (dp.appType === 'backend' && dp.framework) {
          slots.backend = dp.framework;
        } else if (dp.appType === 'mcp') {
          slots.apiType = 'MCP';
        }
        found.push({ slots, priority: k.priority, frameworks: dp.isFlutter ? ['Flutter', 'Dart'] : ['Dart'], fileName: f });
        continue;
      }
    }
    // Content-aware: go.mod backs libraries, backends, CLIs, MCP — not one bucket.
    if (f === 'go.mod') {
      const gp = detectGoProject(cur);
      if (gp) {
        const slots: Record<string, string> = {
          mainLanguage: 'Go',
          packageManager: 'go modules',
          buildTool: 'go build',
        };
        if (gp.appType === 'backend' && gp.framework) {
          slots.backend = gp.framework;
        } else if (gp.appType === 'cli' && gp.framework) {
          slots.framework = gp.framework;
        } else if (gp.appType === 'mcp') {
          slots.apiType = 'MCP';
        }
        const frameworks = gp.framework ? [gp.framework, 'Go'] : ['Go'];
        found.push({ slots, priority: k.priority, frameworks, fileName: f });
        continue;
      }
    }
    // Content-aware: Gemfile alone ≠ Rails (Ruby Edition).
    if (f === 'Gemfile' || f === 'Gemfile.lock' || f === 'gems.rb') {
      const rp = detectRubyProject(cur);
      if (rp) {
        const slots: Record<string, string> = {
          mainLanguage: 'Ruby',
          packageManager: rp.packageManager,
        };
        if (rp.appType === 'backend' && rp.framework) {
          slots.backend = rp.framework;
        } else if (rp.appType === 'cli' && rp.framework) {
          slots.framework = rp.framework;
        } else if (rp.appType === 'mcp') {
          slots.apiType = 'MCP';
        }
        const frameworks = rp.framework ? [rp.framework, 'Ruby'] : ['Ruby'];
        found.push({
          slots,
          priority: k.priority ?? 35,
          frameworks,
          fileName: f,
        });
        continue;
      }
    }
    // Content-aware: Package.swift alone ≠ app (Swift Edition).
    if (f === 'Package.swift') {
      const sp = detectSwiftProject(cur);
      if (sp) {
        const slots: Record<string, string> = {
          mainLanguage: 'Swift',
          packageManager: sp.packageManager === 'xcode' ? 'Xcode' : 'spm',
          buildTool: 'swift build',
        };
        if (sp.appType === 'backend' && sp.framework) {
          slots.backend = sp.framework;
        } else if (sp.appType === 'cli' && sp.framework) {
          slots.framework = sp.framework;
        } else if (sp.appType === 'mcp') {
          slots.apiType = 'MCP';
        } else if (sp.appType === 'app' && sp.framework) {
          slots.framework = sp.framework;
        }
        const frameworks = sp.framework ? [sp.framework, 'Swift'] : ['Swift'];
        found.push({
          slots,
          priority: k.priority ?? 36,
          frameworks,
          fileName: f,
        });
        continue;
      }
    }
    // Content-aware: pom / gradle alone ≠ type (JVM Edition). One brand JVM;
    // Android/KMP are facets. Version catalogs + multi-module via detectJvmProject.
    if (
      f === 'pom.xml' ||
      f === 'build.gradle' ||
      f === 'build.gradle.kts' ||
      f === 'settings.gradle' ||
      f === 'settings.gradle.kts'
    ) {
      if (jvmDirsDone.has(cur)) {continue;}
      jvmDirsDone.add(cur);
      const jv = detectJvmProject(cur);
      if (jv) {
        const slots: Record<string, string> = {
          mainLanguage: jv.mainLanguage === 'Java/Kotlin' ? 'Kotlin' : jv.mainLanguage,
          packageManager: jv.buildTool === 'maven' ? 'maven' : 'gradle',
          buildTool: jv.buildTool,
        };
        if (jv.appType === 'backend' && jv.framework) {
          slots.backend = jv.framework;
        } else if (jv.appType === 'cli' && jv.framework) {
          slots.framework = jv.framework;
        } else if (jv.appType === 'mcp') {
          slots.apiType = 'MCP';
        } else if (jv.appType === 'mobile') {
          slots.framework = jv.framework || 'Android';
        }
        if (jv.facets.includes('multiplatform')) {
          slots.framework = jv.framework || 'Kotlin Multiplatform';
        }
        const frameworks = jv.framework
          ? [jv.framework, 'JVM', jv.mainLanguage === 'Kotlin' ? 'Kotlin' : 'Java']
          : ['JVM', jv.mainLanguage === 'Kotlin' ? 'Kotlin' : 'Java'];
        found.push({
          slots,
          priority: k.priority ?? 35,
          frameworks,
          fileName: f,
        });
        continue;
      }
    }
    found.push({ slots: (k.slots as Record<string, string>) || {}, priority: k.priority, frameworks: k.frameworks, fileName: f });
  }
  return found;
}

/** Layer 1B — source extensions present. Extensions assert LANGUAGE only: a .py
 *  reliably means Python, but a stray .tsx must not assert "React" (frameworks
 *  come from config files, Layer 1A). */
function scanExtensions(projectDir: string): FoundFormat[] {
  const found: FoundFormat[] = [];
  for (const ext of withRepoRoot(projectDir, () => collectExtensions(projectDir, 2, { count: 0 }))) {
    const k = KNOWLEDGE_BASE[`*${ext}`];
    const lang = (k?.slots as Record<string, string> | undefined)?.mainLanguage;
    if (lang) {found.push({ slots: { mainLanguage: lang }, priority: k.priority, frameworks: k.frameworks });}
  }
  return found;
}

/** Scan formats → priority-wins slot recommendations. */
export function turboCatScan(projectDir: string): TurboCatResult {
  const found = [...scanConfigFiles(projectDir), ...scanExtensions(projectDir)];

  // Analyze — highest priority wins each slot (deterministic, order-independent).
  // Skip ambiguous multi-option hints (e.g. "npm/yarn/pnpm") — only confident
  // single values fill; v6's specific detection handles the rest.
  const slotFills: Record<string, string> = {};
  const slotPrio: Record<string, number> = {};
  const fw = new Set<string>();
  for (const item of found) {
    item.frameworks.forEach((f) => fw.add(f));
    for (const [slot, val] of Object.entries(item.slots)) {
      if (val && !val.includes('/') && (!(slot in slotFills) || item.priority > slotPrio[slot])) {
        slotFills[slot] = val;
        slotPrio[slot] = item.priority;
      }
    }
  }
  // Per-format breakdown — real files only, deduped, deterministic order
  // (priority desc, then name) regardless of filesystem enumeration order.
  const seen = new Map<string, DiscoveredFormat>();
  for (const item of found) {
    if (!item.fileName) {continue;}
    if (!seen.has(item.fileName)) {
      seen.set(item.fileName, {
        fileName: item.fileName,
        category: categoryFor(item.slots, item.frameworks),
        priority: item.priority,
      });
    }
  }
  const discoveredFormats = [...seen.values()].sort(
    (a, b) => b.priority - a.priority || a.fileName.localeCompare(b.fileName),
  );

  // Deterministic stack signature: language + framework, lowercased, dash-joined.
  const sigParts: string[] = [];
  if (slotFills.mainLanguage) {sigParts.push(slotFills.mainLanguage.toLowerCase());}
  if (slotFills.framework) {sigParts.push(slotFills.framework.toLowerCase());}
  const stackSignature = sigParts.length > 0 ? sigParts.join('-') : 'unknown-stack';

  return { slotFills, frameworks: [...fw], confirmedCount: found.length, discoveredFormats, stackSignature };
}

/** Turbo-Cat slot fills shaped as a v6 .faf partial ({ project, stack }) for fillEmpties. */
export function turboCatSlots(projectDir: string): { project?: Record<string, string>; stack?: Record<string, string> } {
  const { slotFills } = turboCatScan(projectDir);
  const out: { project?: Record<string, string>; stack?: Record<string, string> } = {};
  for (const [key, value] of Object.entries(slotFills)) {
    const target = SLOT_MAP[key];
    if (!target || !value) {continue;}
    const [section, field] = target;
    if (section === 'project') {(out.project ??= {})[field] = value;}
    else {(out.stack ??= {})[field] = value;}
  }
  return out;
}

function collectExtensions(dir: string, depth: number, budget: { count: number }): Set<string> {
  const exts = new Set<string>();
  if (depth < 0 || budget.count > 400) {return exts;}
  const entries = readRepoDir(dir);
  if (!entries) {return exts;}
  for (const e of entries) {
    if (budget.count++ > 400) {break;}
    if (!e.isDirectory()) {
      const ext = extname(e.name);
      if (SCAN_EXTS.has(ext)) {exts.add(ext);}
      continue;
    }
    if (IGNORE.has(e.name) || e.name.startsWith('.')) {continue;}
    for (const x of collectExtensions(join(dir, e.name), depth - 1, budget)) {exts.add(x);}
  }
  return exts;
}
